import React, { FC, useEffect, useMemo } from 'react';
import styles from '../index.module.scss';
import { Form, Skeleton } from 'src/common';
import { useTranslation } from 'react-i18next';
import { useSsoConfig, useSsoEmailDomain } from 'src/models/company/Security/hooks';
import SsoConfigForm from './SsoConfigForm';
import EmailDomainPanel from './EmailDomainPanel';
import SsoProviderForm from './SsoProviderForm';
import SsoMetaInfo from './SsoMetaInfo';
import { SsoProvider } from 'src/models/users/types';

export interface IFormState {
  provider: SsoProvider | undefined;
  activeSSO: boolean;
  enforceSSO: boolean;
  metadataUrl: string;
}

export const SsoDesc: FC = () => {
  const { t } = useTranslation();
  const { loading: ssoConfigLoading, provider, activeSSO, enforceSSO } = useSsoConfig();
  const { data: emailDomain, loading: ssoDomainLoading } = useSsoEmailDomain();
  const [form] = Form.useForm<IFormState>();
  const ssoProvider = useMemo(
    () =>
      provider === undefined ? undefined : provider === SsoProvider.google ? SsoProvider.google : SsoProvider.microsoft,
    [provider]
  );

  const initialValues: IFormState = useMemo(() => {
    return {
      provider: ssoProvider,
      activeSSO: !!activeSSO,
      enforceSSO: !!enforceSSO,
      metadataUrl: ''
    };
  }, [activeSSO, enforceSSO, ssoProvider]);

  useEffect(() => {
    form.setFieldsValue({ activeSSO: !!activeSSO, enforceSSO: !!enforceSSO, provider: ssoProvider });
  }, [activeSSO, enforceSSO, ssoProvider, form]);

  return (
    <>
      {ssoConfigLoading ? (
        <Skeleton active loading />
      ) : (
        <div className={styles.container}>
          <h2 className={styles.subTitle}>{t('Security.singleSignOn')}</h2>
          <p>{t('Security.singleSignOnDescription')}</p>
          {/*<LearnMoreLink className={styles.link} href={'#'} title={t('Security.learnMoreAboutSSO')} />*/}
          <Form initialValues={initialValues} form={form} layout={'vertical'} className={styles.content}>
            <EmailDomainPanel loading={ssoDomainLoading} label={emailDomain || ''} />
            <SsoProviderForm form={form} currentSsoProvider={ssoProvider} />
            <Form.Item noStyle dependencies={['provider']}>
              {ctx =>
                ctx.getFieldValue('provider') === SsoProvider.microsoft ? (
                  <>
                    <p>{t('Security.ssoConnectionDesc')}</p>
                    <SsoMetaInfo />
                  </>
                ) : null
              }
            </Form.Item>
            <SsoConfigForm form={form} currentSsoProvider={ssoProvider} />
          </Form>
        </div>
      )}
    </>
  );
};

export default SsoDesc;
